import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import ArialNormal from "../../../../utils/Customfonts/ARIAL.TTF";
import ArialBold from "../../../../utils/Customfonts/ARIALBD.TTF";
import ArialItalic from "../../../../utils/Customfonts/ARIALI.TTF";
import ArialBoldItalic from "../../../../utils/Customfonts/ARIALBI.TTF";

const DYC_LOGO = "/img/dycgpl-logo.webp";

export const customFontFamily = {
  bold: "bold",
  normal: "normal",
  boldFont: "Arial",
  normalFont: "Arial",
  italic: "italic",
  italicFont: "Arial",
};

// -----------------------------------------------------------------------------
// GLOBAL LAYOUT CONFIG
// -----------------------------------------------------------------------------

export const config = {
  borderMargin: 15,
  contentPadding: 15,

  get margin() {
    return this.borderMargin + this.contentPadding;
  },

  fontSize: 8.5,
  fontSizeSm: 7.5,
  fontSizeLg: 10,
  fontSizeTitle: 16,

  cellPadding: 4,
  sectionGap: 10,
  lineGap: 14,

  headerFontSize: 9,
  headerFont: customFontFamily.boldFont,
  headerFontStyle: customFontFamily.bold,
  headerAlign: "center",
  headerPadding: { top: 5, right: 4, bottom: 5, left: 4 },

  get contentTop() {
    return this.borderMargin + 65;
  },

  get contentBottom() {
    return this.borderMargin + 42;
  },
};

// -----------------------------------------------------------------------------
// COMMON STYLES
// -----------------------------------------------------------------------------

const getBaseStyles = () => ({
  fontSize: config.fontSize,
  cellPadding: config.cellPadding,
  lineColor: [100, 100, 100],
  lineWidth: 0.5,
  textColor: [0, 0, 0],
  fillColor: false,
  font: customFontFamily.normalFont,
  fontStyle: customFontFamily.normal,
  halign: "left",
  valign: "middle",
});

const getHeaderStyles = () => ({
  ...getBaseStyles(),
  font: customFontFamily.boldFont,
  fontStyle: customFontFamily.bold,
  fillColor: [230, 235, 245],
  textColor: [0, 0, 0],
  halign: "center",
  valign: "middle",
});

// -----------------------------------------------------------------------------
// LAYOUT MANAGER
// -----------------------------------------------------------------------------

const layout = {
  doc: null,
  y: 0,
  pageWidth: 0,
  pageHeight: 0,
  contentWidth: 0,

  init(doc) {
    this.doc = doc;
    this.pageWidth = doc.internal.pageSize.getWidth();
    this.pageHeight = doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - config.margin * 2;
    this.y = config.contentTop;
  },

  advance(amount) {
    this.y += amount;
  },

  ensureSpace(requiredHeight = 40) {
    if (this.y + requiredHeight > this.pageHeight - config.contentBottom) {
      this.doc.addPage();
      this.y = config.contentTop;
    }
  },

  afterTable(gap = config.sectionGap) {
    if (this.doc.lastAutoTable) {
      this.y = this.doc.lastAutoTable.finalY + gap;
    }
  },
};

// -----------------------------------------------------------------------------
// FONT LOADING
// -----------------------------------------------------------------------------

const arrayBufferToBase64 = (buffer) => {
  let binary = "";
  const bytes = new Uint8Array(buffer);
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const loadAndAddFont = async (pdfDoc, fontUrl, fontNameTTF, fontName, fontStyle) => {
  try {
    const response = await fetch(fontUrl);
    const buffer = await response.arrayBuffer();
    const base64 = arrayBufferToBase64(buffer);

    pdfDoc.addFileToVFS(`${fontNameTTF}.ttf`, base64);
    pdfDoc.addFont(`${fontNameTTF}.ttf`, fontName, fontStyle);
  } catch (error) {
    console.error(`Font loading failed: ${fontName}`, error);
  }
};

// -----------------------------------------------------------------------------
// HEADER / FOOTER DECORATORS
// -----------------------------------------------------------------------------

const addPageDecorators = (doc, totalPages, data = {}) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page);

    // OUTER BORDER
    doc.setDrawColor(0, 0, 0);
    doc.setLineWidth(0.7);
    doc.rect(
      config.borderMargin,
      config.borderMargin,
      pageWidth - config.borderMargin * 2,
      pageHeight - config.borderMargin * 2
    );

    // FORM NAME
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.setFontSize(15);
    doc.setTextColor(25, 25, 25);
    doc.text(
      "INDUCTION TRAINING CHECKLIST",
      pageWidth / 2,
      config.borderMargin + 32,
      { align: "center" }
    );

    // LOGO
    try {
      const logoSize = 40;
      const logoX = pageWidth - config.borderMargin - logoSize - 8;
      const logoY = config.borderMargin + 6;

      try {
        doc.addImage(DYC_LOGO, "WEBP", logoX, logoY, logoSize, logoSize);
      } catch (_) {
        try {
          doc.addImage(DYC_LOGO, "PNG", logoX, logoY, logoSize, logoSize);
        } catch (__) {
          doc.addImage(DYC_LOGO, logoX, logoY, logoSize, logoSize);
        }
      }
    } catch (_) {
      // Logo optional
    }

    // HEADER DIVIDER LINE
    doc.setLineWidth(0.5);
    doc.line(
      config.borderMargin,
      config.borderMargin + 50,
      pageWidth - config.borderMargin,
      config.borderMargin + 50
    );

    // FOOTER
    doc.setFont(customFontFamily.normalFont, customFontFamily.normal);
    doc.setFontSize(7.5);
    doc.setTextColor(50, 50, 50);

    const docCode = `${data.docCode || "DYC/FMT-MR/25"} Rev.:${data.revNo || "00"} Eff. Date-${data.effDate || "24.05.2023"}`;
    const footerY1 = pageHeight - config.borderMargin - 16;
    const footerY2 = pageHeight - config.borderMargin - 6;

    // Line 1 Left: Document Code / Revision / Eff. Date
    doc.text(docCode, config.borderMargin + 8, footerY1);

    // Line 2 Left: Company General Use
    doc.text(
      "Company General Use. All Rights Reserved By DYC Global Pvt Ltd.",
      config.borderMargin + 8,
      footerY2
    );

    // Right: Page number
    doc.setFont(customFontFamily.boldFont, customFontFamily.bold);
    doc.text(
      `Page ${page} of ${totalPages}`,
      pageWidth - config.borderMargin - 60,
      footerY2
    );
  }
};

// -----------------------------------------------------------------------------
// SECTION 1: EMPLOYEE INFO HEADER TABLE
// -----------------------------------------------------------------------------

const drawEmployeeHeaderTable = (doc, data = {}) => {
  const headerData = [
    [
      { content: "Name of Employee:", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245], cellWidth: layout.contentWidth * 0.2 } },
      { content: data.employeeName || "-", styles: { cellWidth: layout.contentWidth * 0.3 } },
      { content: "Designation:", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245], cellWidth: layout.contentWidth * 0.2 } },
      { content: data.designation || "-", styles: { cellWidth: layout.contentWidth * 0.3 } },
    ],
    [
      { content: "Training Start Date:", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: data.trainingStartDate || "-" },
      { content: "Training End Date:", styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [245, 245, 245] } },
      { content: data.trainingEndDate || "-" },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: headerData,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSize },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
  });

  layout.afterTable(8);
};

// -----------------------------------------------------------------------------
// SECTION 2: CHECKLIST SECTIONS TABLE
// -----------------------------------------------------------------------------

const drawChecklistTable = (doc, sections = [], checklistData = {}) => {
  const tableBody = [];

  sections.forEach((sec) => {
    if (sec.isTextArea) {
      // Section 8: On Job Training For Surveyors
      tableBody.push([
        {
          content: `${sec.sectionNo}`,
          styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 240, 255] },
        },
        {
          content: `${sec.title}`,
          colSpan: 4,
          styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 240, 255] },
        },
      ]);

      const supervisor = checklistData[`${sec.keyPrefix}_supervisor`] || "-";
      const sign = checklistData[`${sec.keyPrefix}_sig`] || "-";
      const details = checklistData[`${sec.keyPrefix}_details`] || "";

      // Row: Write below details | Supervisor | Sign
      tableBody.push([
        { content: "-", styles: { halign: "center" } },
        { content: "Write below details", colSpan: 2, styles: { font: customFontFamily.normalFont, fontStyle: customFontFamily.normal } },
        { content: `${supervisor}`, styles: { valign: "middle" } },
        { content: `${sign}`, styles: { valign: "middle", halign: "center" } },
      ]);

      // Row: Multi-line description box
      tableBody.push([
        {
          content: `Mention Details- Details such as supervisor's name, vendor details, scopes of training, dates of training, observations etc.\n\n${details}`,
          colSpan: 5,
          styles: { minCellHeight: 40, valign: "top", fontSize: config.fontSizeSm, textColor: [30, 30, 30] },
        },
      ]);
    } else {
      // Regular Sections 1 to 7
      tableBody.push([
        {
          content: `${sec.sectionNo}`,
          styles: { halign: "center", font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 240, 255] },
        },
        {
          content: sec.title,
          colSpan: 4,
          styles: { font: customFontFamily.boldFont, fontStyle: customFontFamily.bold, fillColor: [230, 240, 255] },
        },
      ]);

      (sec.items || []).forEach((item) => {
        const dtKey = `${item.keyPrefix}Dt`;
        const rpKey = `${item.keyPrefix}Rp`;

        tableBody.push([
          { content: item.subSr ? `${item.subSr}` : "", styles: { halign: "center", textColor: [60, 60, 60] } },
          { content: item.label || "" },
          { content: item.reference || "", styles: { fontSize: config.fontSizeSm, textColor: [80, 80, 80] } },
          { content: checklistData[dtKey] || "-", styles: { halign: "center" } },
          { content: checklistData[rpKey] || "-", styles: { halign: "left" } },
        ]);
      });
    }
  });

  autoTable(doc, {
    startY: layout.y,
    head: [["Sr. No.", "Induction Details", "Reference", "Date", "Responsible Person"]],
    body: tableBody,
    theme: "grid",
    styles: { ...getBaseStyles() },
    headStyles: getHeaderStyles(),
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: layout.contentWidth * 0.08, halign: "center" },
      1: { cellWidth: layout.contentWidth * 0.42 },
      2: { cellWidth: layout.contentWidth * 0.22 },
      3: { cellWidth: layout.contentWidth * 0.12, halign: "center" },
      4: { cellWidth: layout.contentWidth * 0.16 },
    },
  });

  layout.afterTable(10);
};

// -----------------------------------------------------------------------------
// SECTION 3: SIGNATURES SECTION
// -----------------------------------------------------------------------------

const drawSignaturesSection = (doc, data = {}, checklistData = {}) => {
  layout.ensureSpace(65);

  const empName = checklistData["empSigName"] || data.employeeName || "";
  const empDate = checklistData["empSigDate"] || "";
  const mgrName = checklistData["mgrSigName"] || "";
  const mgrDate = checklistData["mgrSigDate"] || "";

  const sigBody = [
    [
      {
        content: `Signature : ______________________\n\nName of Employee : ${empName}\n\nDate : ${empDate}`,
        styles: { minCellHeight: 50, valign: "middle" },
      },
      {
        content: `Signature : ______________________\n\nName of Manager : ${mgrName}\n\nDate : ${mgrDate}`,
        styles: { minCellHeight: 50, valign: "middle" },
      },
    ],
  ];

  autoTable(doc, {
    startY: layout.y,
    body: sigBody,
    theme: "grid",
    styles: { ...getBaseStyles(), fontSize: config.fontSize },
    margin: {
      top: config.contentTop,
      bottom: config.contentBottom,
      left: config.margin,
      right: config.margin,
    },
    columnStyles: {
      0: { cellWidth: layout.contentWidth * 0.5 },
      1: { cellWidth: layout.contentWidth * 0.5 },
    },
  });

  layout.afterTable(10);
};

// -----------------------------------------------------------------------------
// MAIN PDF GENERATOR EXPORT
// -----------------------------------------------------------------------------

export const generateEmpTrainingPDF = async (inputStates = {}, checklistData = {}, sections = []) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
    compress: true,
  });

  await Promise.all([
    loadAndAddFont(doc, ArialNormal, "Arial-Normal", customFontFamily.normalFont, customFontFamily.normal),
    loadAndAddFont(doc, ArialBold, "Arial-Bold", customFontFamily.boldFont, customFontFamily.bold),
    loadAndAddFont(doc, ArialItalic, "Arial-Italic", customFontFamily.italicFont, customFontFamily.italic),
    loadAndAddFont(doc, ArialBoldItalic, "Arial-BoldItalic", customFontFamily.normalFont, "bolditalic"),
  ]);

  doc.setFont(customFontFamily.normalFont, customFontFamily.normal);

  layout.init(doc);
  layout.y = config.contentTop + 10;

  // 1. Employee Header Information
  drawEmployeeHeaderTable(doc, inputStates);

  // 2. Checklist Sections Table
  drawChecklistTable(doc, sections, checklistData);

  // 3. Signatures Box
  drawSignaturesSection(doc, inputStates, checklistData);

  // 4. Outer Borders, Header Branding, Footers & Page numbers
  const totalPages = doc.internal.getNumberOfPages();
  addPageDecorators(doc, totalPages, inputStates);

  return doc;
};

export default generateEmpTrainingPDF;
